<template>
  <div class="modern-main">
    <!-- 固定标题栏 -->
    <AppHeader
      @theme-toggle="handleThemeToggle"
      @open-api-site="handleOpenApiSite"
      @show-login="handleShowLogin"
      @logout="handleLogout"
    />

    <!-- 两列布局：文本编辑区(中间) + 语音主播选择区(右侧)  v-show="page.asideIndex === '1'" -->
    <div class="two-column-layout">
      <!-- router路由渲染page -->
      <router-view class="router-view"></router-view>
    </div>

    <!-- 备案信息 - 只在指定页面显示 -->
    <footer v-if="$route.meta.showIcp" class="site-footer icp-footer" aria-label="备案信息">
      <div class="icp-info">
        <p>鄂ICP备2025123562号</p>
        <p>
          <a href="https://beian.miit.gov.cn/" target="_blank" rel="noopener noreferrer">工业和信息化部网站备案系统</a>
        </p>
      </div>
    </footer>

    <!-- 登录弹窗 -->
    <LoginDialog
      v-model="showLogin"
      @login-success="handleLoginSuccess"
    />
  </div>
</template>

<script setup lang="ts">
import {ref, watch, computed, onMounted, onUnmounted} from "vue";
// 统一Store导入
import {useTTSStore} from "@/store";
import { useUserStore } from "@/store/user";
import { useTheme } from "@/composables/useTheme";
import { useMessage } from "@/composables/useMessage";
import { invariantDev } from "@/utils/invariant";

// 导入AppHeader组件
import { AppHeader } from "@/layouts/app-header";
import LoginDialog from '@/components/ui/LoginDialog.vue';

// 使用新的模块化Store
const ttsStore = useTTSStore();
const userStore = useUserStore();
const theme = useTheme();
const message = useMessage();

// Fail-Fast检查 - 确保关键依赖已初始化
invariantDev(ttsStore, 'TTSStore must be available');
invariantDev(userStore, 'UserStore must be available');
invariantDev(theme, 'Theme composable must be available');
invariantDev(theme.isDarkTheme, 'Theme state must be initialized');

// 从Store中获取响应式数据
const inputs = ttsStore.inputs;

// 添加缺失的状态
const isSSMLMode = computed(() => ttsStore.isSSMLMode);

// 监听SSML模式变化
watch(isSSMLMode, (newValue) => {
  if (!newValue) {
    // 切换到纯文本模式时，重置手动编辑状态
    ttsStore.inputs.isSSMLManuallyEdited = false;
    console.log('切换到纯文本模式，重置SSML编辑状态');
  } else {
    // 切换到SSML模式时，使用store的方法生成完整的SSML
    if (!ttsStore.inputs.isSSMLManuallyEdited && inputs.inputValue) {
      ttsStore.setSSMLValue(inputs.inputValue, true);
      console.log('已生成完整的SSML格式');
    }
  }
});

// AppHeader 事件处理函数
const handleThemeToggle = () => {
  console.log('handleThemeToggle 被调用');
  theme.toggleTheme();
};

const handleOpenApiSite = () => {
  console.log('handleOpenApiSite 被调用');
  window.open('https://api.tts88.top', '_blank');
};

// 登录弹窗状态
const showLogin = ref(false);

const handleShowLogin = () => {
  console.log('handleShowLogin 被调用');
  showLogin.value = true;
};

// 登录成功处理
const handleLoginSuccess = () => {
  showLogin.value = false;
  // 触发全局登录成功事件，供其他组件（如推广落地页）监听
  window.dispatchEvent(new CustomEvent('login-success'));
};

const handleLogout = async () => {
  try {
    await userStore.logout();
    // 消息已在 userStore.logout() 中显示，这里不需要重复显示
  } catch (error) {
    message.showError('退出登录失败');
  }
};

// 监听全局登录弹窗事件（供子组件触发）
const handleGlobalShowLogin = () => {
  showLogin.value = true;
};

onMounted(() => {
  window.addEventListener('show-login-dialog', handleGlobalShowLogin);
});

onUnmounted(() => {
  window.removeEventListener('show-login-dialog', handleGlobalShowLogin);
});
</script>

<style>
/* 全局样式导入 */
@import '@/assets/styles/main/MainStyles.css';
</style>

<style scoped>
/* 组件作用域样式导入 */
@import '@/assets/styles/main/MainScopedStyles.css';

.router-view {
  width: 100%;
}

/* 主界面容器 */
.modern-main {
  padding: 0;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  background-color: transparent;
  overflow-x: auto; /* 允许横向滚动，支持固定宽度布局 */
}

/* 两列布局 */
.two-column-layout {
  display: flex;
  width: 100%;
  max-width: 1600px; /* 限制最大宽度 */
  margin: 0 auto; /* 居中显示 */
  justify-content: stretch;
  background-color: transparent;
  overflow-x: auto; /* 允许横向滚动，支持固定宽度布局 */
  overflow-y: hidden; /* 保持纵向不滚动 */
  position: relative;
  flex: 1;
}



/* 移除 1050px 断点的 flex-direction: column 切换，保持横向布局 */
/* 这样可以避免批量转换页面的布局被破坏 */
/*
@media (max-width: 1050px) {
  .two-column-layout {
    flex-direction: column;
  }
}
*/

@media (max-width: 768px) {
  .modern-main {
    padding: 0;
  }

  .two-column-layout {
    padding: 0;
  }
}

/* 页脚样式 */
.site-footer {
  margin: 20px auto 0;
  width: 100%;
  max-width: 1600px;
  padding: 20px;
  background-color: var(--card-background);
  border-radius: var(--border-radius-large);
  box-shadow: var(--shadow-medium);
  border: 1px solid var(--border-color);
}

.icp-footer {
  text-align: center;
  color: #888;
}

.icp-info {
  margin: 0;
  padding: 12px 0;
  font-size: 14px;
  color: #888;
}

.icp-info a {
  color: #888;
  text-decoration: underline;
}

@media (max-width: 768px) {
  .site-footer {
    margin-top: 10px;
    border-radius: var(--border-radius-medium);
    padding: 15px;
  }
}
</style>
